"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useAuth } from "@/hooks/useAuth";
import { FullConversationType, FullMessageType } from "@/types";
import { Search, MessageSquarePlus } from "lucide-react";
import NewMessageModal from "@/components/messages/NewMessageModal";

// Socket functionality removed - using API calls only
// Simplified version without complex component dependencies

interface MessagesClientProps {
  initialConversationId?: string | number;
}

export default function MessagesClient({ initialConversationId }: MessagesClientProps) {
  const { user, loading } = useAuth();
  const router = useRouter();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      router.replace('/login');
    }
  }, [loading, user, router]);

  // Don't render anything if not authenticated
  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }
  
  const [conversations, setConversations] = useState<FullConversationType[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<FullConversationType | null>(null);
  const [messages, setMessages] = useState<FullMessageType[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [isNewMessageModalOpen, setIsNewMessageModalOpen] = useState(false);

  // Fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await axios.get(`${process?.env?.NEXT_PUBLIC_API_URL}/messages/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        setConversations(response.data);
        
        // Handle initial conversation selection
        if (initialConversationId) {
          const conversation = response?.data?.find((c: FullConversationType) =>
            c.id?.toString() === initialConversationId?.toString()
          );
          if (conversation) {
            setSelectedConversation(conversation);
            console.log(`[Navigation] Selected conversation: ${conversation.id}`);
          } else {
            console.log(`[Navigation] Conversation not found for initial ID: ${initialConversationId}`);
          }
        } else if (response?.data?.length > 0 && !selectedConversation && !initialConversationId) {
          // If no conversation is selected and no initialConversationId is provided,
          // redirect to the first conversation
          router.push(`/messages/${response.data?.[0]?.id}`);
        }
      } catch (error: unknown) {
        console.error("Error fetching conversations:", error);
        toast.error("Failed to load conversations");
      }
    };

    if (user?.email) fetchConversations();
  }, [user?.email, selectedConversation, initialConversationId, router]);

  // Fetch messages for selected conversation
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedConversation) return;
      
      try {
        const response = await axios.get(`${process?.env?.NEXT_PUBLIC_API_URL}/messages/conversations/${selectedConversation.id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        setMessages(response?.data?.messages || []);
        console.log(`[Messages] Loaded ${response?.data?.messages?.length || 0} messages for conversation ${selectedConversation.id}`);
      } catch (error) {
        console.error("Error fetching messages:", error);
        toast.error("Failed to load messages");
      }
    };

    fetchMessages();
  }, [selectedConversation]);

  // Send message function (API only, no socket)
  const sendMessage = async () => {
    if (!selectedConversation || !user || !newMessage.trim()) return;

    try {
      const response = await axios.post(`${process?.env?.NEXT_PUBLIC_API_URL}/messages/`, {
        content: newMessage,
        conversationId: selectedConversation.id?.toString(),
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      // Add the new message to the local state
      setMessages(prev => [...prev, response?.data?.message]);
      setNewMessage("");
      
      console.log('[Messages] Message sent successfully');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  // Create new conversation
  const handleStartConversation = async (userId: number) => {
    try {
      const response = await axios.post(`${process?.env?.NEXT_PUBLIC_API_URL}/messages/conversations`, {
        userId: userId?.toString()
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      const newConversation = response.data;
      
      // Add to conversations list
      setConversations(prev => [newConversation, ...prev]);
      
      // Navigate to the new conversation
      router.push(`/messages/${newConversation.id}`);
      
      console.log('[Messages] New conversation created:', newConversation.id);
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to create conversation');
      throw error;
    }
  };

  const filteredConversations = conversations.filter(conversation =>
    conversation.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conversation?.users?.some(user => 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <div className="w-80 border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold">Messages</h1>
            <button
              onClick={() => setIsNewMessageModalOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <MessageSquarePlus className="h-5 w-5" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations?.map((conversation) => (
            <div
              key={conversation.id}
              onClick={() => setSelectedConversation(conversation)}
              className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                selectedConversation?.id === conversation.id ? 'bg-blue-50' : ''
              }`}
            >
              <div className="font-medium">
                {conversation.name || (Array.isArray(conversation.users) ? conversation?.users?.map(u => u.name).join(', ') : 'Unknown')}
              </div>
              <div className="text-sm text-gray-500 truncate">
                {conversation.messages[0]?.body || 'No messages yet'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold">
                {selectedConversation.name || (Array.isArray(selectedConversation.users) ? selectedConversation?.users?.map(u => u.name).join(', ') : 'Unknown')}
              </h2>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages?.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.senderId?.toString() === user?.id?.toString() ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message.senderId?.toString() === user?.id?.toString()
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-900'
                    }`}
                  >
                    <div className="text-sm">{message.body}</div>
                    <div className="text-xs opacity-75 mt-1">
                      {new Date(message.createdAt).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e?.target?.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageSquarePlus className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>

      {/* New Message Modal */}
      <NewMessageModal
        isOpen={isNewMessageModalOpen}
        onClose={() => setIsNewMessageModalOpen(false)}
        onStartConversation={handleStartConversation}
      />
    </div>
  );
}